export const NEWS_CATEGORIES = [
    { id: "technology", builderId: "fbdb7c85146f4fa0ad6f27adb9768f80" },
    { id: "creative", builderId: "dbc5bed966c54da0b37ab725f843916c" },
    { id: "marketing", builderId: "a4d22734e1a74d4eba0b4234de01a2c6" },
];

/** @type {Record<string, string>} */
const NEWS_CATEGORY_ID_ALIASES = {
    technologyecommerce: "technology",
    "technology + ecommerce": "technology",
    "technology + engineering": "technology",
    creativedesign: "creative",
    "creative + design": "creative",
    "creative + experience": "creative",
    marketinggrowth: "marketing",
    "marketing + growth": "marketing",
    "marketing + performance": "marketing",
};

/**
 * @param {unknown} value
 */
function normalizeForLookup(value) {
    return typeof value === "string" ? value.trim().toLowerCase() : "";
}

/**
 * @param {unknown} value
 */
function normalizeEntityId(value) {
    return typeof value === "string" ? value.trim() : "";
}

/**
 * @param {unknown} value
 */
function toFallbackId(value) {
    const entityId = normalizeEntityId(value);

    if (!entityId) {
        return "";
    }

    const encodedEntityId = Array.from(new TextEncoder().encode(entityId), (byte) =>
        byte.toString(16).padStart(2, "0"),
    ).join("");

    return `category--${encodedEntityId}`;
}

/**
 * @param {{ id?: unknown, name?: unknown }[]} categoryEntities
 */
export function toNewsCategoryOptions(categoryEntities) {
    const entitiesById = new Map();

    for (const categoryEntity of categoryEntities) {
        const id = normalizeEntityId(categoryEntity?.id);
        const name = categoryEntity?.name;

        if (id && typeof name === "string" && name.trim() && !entitiesById.has(id)) {
            entitiesById.set(id, name);
        }
    }

    const options = NEWS_CATEGORIES.flatMap(({ id, builderId }) => {
        const name = entitiesById.get(builderId);
        return name === undefined ? [] : [{ id, name }];
    });
    const knownBuilderIds = new Set(NEWS_CATEGORIES.map(({ builderId }) => builderId));

    for (const [builderId, name] of entitiesById) {
        if (!knownBuilderIds.has(builderId)) {
            options.push({ id: toFallbackId(builderId), name });
        }
    }

    return options;
}

/**
 * @param {unknown} value
 */
export function normalizeNewsCategoryFilter(value) {
    const normalizedValue = normalizeForLookup(value);

    if (!normalizedValue) {
        return "";
    }

    const category = NEWS_CATEGORIES.find(
        ({ id, builderId }) =>
            normalizeForLookup(id) === normalizedValue ||
            normalizeForLookup(builderId) === normalizedValue,
    );

    if (category) {
        return category.id;
    }

    const alias = NEWS_CATEGORY_ID_ALIASES[normalizedValue];
    if (alias) {
        return alias;
    }

    return normalizedValue.startsWith("category--")
        ? normalizedValue
        : toFallbackId(normalizeEntityId(value));
}

/**
 * @param {unknown} categoryEntityId
 * @param {unknown} filterValue
 */
export function newsCategoryMatches(categoryEntityId, filterValue) {
    const entityId = normalizeEntityId(categoryEntityId);
    const normalizedFilter = normalizeNewsCategoryFilter(filterValue);
    const category = NEWS_CATEGORIES.find(({ id }) => id === normalizedFilter);

    if (category) {
        return entityId === category.builderId;
    }

    return toFallbackId(entityId) === normalizedFilter;
}

/**
 * @param {unknown} categoryEntityId
 */
export function toNewsCategoryQuery(categoryEntityId) {
    const entityId = normalizeEntityId(categoryEntityId);
    const category = NEWS_CATEGORIES.find(({ builderId }) => builderId === entityId);

    return category?.id ?? toFallbackId(entityId);
}
